import React from 'react'
// @floating-ui
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react'
// framer-motion
import { cn } from '@utils/cn'
// utils
import merge from 'deepmerge'
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion'

import { DialogBody, DialogBodyProps } from './DialogBody'
import { DialogFooter, DialogFooterProps } from './DialogFooter'
// dialog components
import { DialogHeader, DialogHeaderProps } from './DialogHeader'
// types
import type { NewAnimatePresenceProps } from './generic'
import type { animate, children, className, dismiss, handler, open, size } from './types'

export interface DialogProps extends React.ComponentProps<'div'> {
  open: open
  handler: handler
  size?: size
  dismiss?: dismiss
  animate?: animate
  className?: className
  children: children
}

const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(({ open, handler, size = 'md', dismiss, animate, className, children, ...rest }, ref) => {
  // 2. set default props
  handler = handler ?? undefined
  dismiss = dismiss ?? undefined

  // 3. set styles
  const backdropClasses = 'grid place-items-center fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-60 backdrop-blur-xs'

  // Размеры модального окна
  const sizeClasses = {
    xs: 'w-full md:w-3/5 lg:w-2/5 2xl:w-1/4 min-w-[80%] md:min-w-[60%] lg:min-w-[40%] 2xl:min-w-[25%] max-w-[80%] md:max-w-[60%] lg:max-w-[40%] 2xl:max-w-[25%]',
    sm: 'w-full md:w-2/3 lg:w-2/4 2xl:w-1/3 min-w-[80%] md:min-w-[66.666667%] lg:min-w-[50%] 2xl:min-w-[33.333333%] max-w-[80%] md:max-w-[66.666667%] lg:max-w-[50%] 2xl:max-w-[33.333333%]',
    md: 'w-full md:w-2/3 lg:w-2/4 2xl:w-1/3 min-w-[80%] md:min-w-[66.666667%] lg:min-w-[50%] 2xl:min-w-[33.333333%] max-w-[80%] md:max-w-[66.666667%] lg:max-w-[50%] 2xl:max-w-[33.333333%]',
    lg: 'w-full md:w-3/4 lg:w-3/5 2xl:w-2/5 min-w-[90%] md:min-w-[75%] lg:min-w-[60%] 2xl:min-w-[40%] max-w-[90%] md:max-w-[75%] lg:max-w-[60%] 2xl:max-w-[40%]',
    xl: 'w-full md:w-4/5 lg:w-4/5 2xl:w-3/5 min-w-[95%] md:min-w-[80%] lg:min-w-[80%] 2xl:min-w-[60%] max-w-[95%] md:max-w-[80%] lg:max-w-[80%] 2xl:max-w-[60%]',
    xxl: 'w-full h-full',
  }

  const baseContainerClasses = 'relative bg-white m-4 rounded-lg shadow-2xl text-blue-gray-500 antialiased outline-none ring-0 focus:outline-none'
  const dialogClasses = cn(baseContainerClasses, sizeClasses[size] || sizeClasses.md, className)

  // 4. set animation
  const animation = {
    unmount: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.3,
      },
    },
    mount: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  }
  const backdropAnimation = {
    unmount: {
      opacity: 0,
      transition: {
        delay: 0.2,
      },
    },
    mount: {
      opacity: 1,
    },
  }

  const appliedAnimation = animate ? merge(animation, animate) : animation

  // 5. set @floating-ui
  const { refs, context } = useFloating({
    open,
    onOpenChange: handler,
  })

  const id = useId()
  const labelId = `${id}-label`
  const descriptionId = `${id}-description`

  const { getFloatingProps } = useInteractions([useClick(context), useRole(context), useDismiss(context, dismiss)])

  const mergedRef = useMergeRefs([ref, refs.setFloating])

  // 6. Create an instance of AnimatePresence because of the types issue with the children
  const NewAnimatePresence: React.FC<NewAnimatePresenceProps> = AnimatePresence

  // 7. return
  return (
    <LazyMotion features={domAnimation}>
      <FloatingPortal>
        <NewAnimatePresence>
          {open && (
            <FloatingOverlay
              style={{
                zIndex: 9999,
                paddingRight: 0,
              }}
              lockScroll
            >
              <FloatingFocusManager context={context}>
                <m.div
                  className={size === 'xxl' ? '' : backdropClasses}
                  initial="unmount"
                  exit="unmount"
                  animate={open ? 'mount' : 'unmount'}
                  variants={backdropAnimation}
                  transition={{ duration: 0.2 }}
                >
                  <m.div
                    {...getFloatingProps({
                      ...rest,
                      ref: mergedRef,
                      className: dialogClasses,
                      'aria-labelledby': labelId,
                      'aria-describedby': descriptionId,
                    })}
                    initial="unmount"
                    exit="unmount"
                    animate={open ? 'mount' : 'unmount'}
                    variants={appliedAnimation}
                  >
                    {children}
                  </m.div>
                </m.div>
              </FloatingFocusManager>
            </FloatingOverlay>
          )}
        </NewAnimatePresence>
      </FloatingPortal>
    </LazyMotion>
  )
})

Dialog.displayName = 'Dialog'

export { Dialog, DialogBody, DialogFooter, DialogHeader }
export type { DialogBodyProps, DialogFooterProps, DialogHeaderProps }
export default Object.assign(Dialog, {
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
})
